import React, { useEffect, useState } from "react";
import { ChevronDown, Network, ThumbsUp, ThumbsDown, Check, X, Sparkles, GitBranch, Target, Search } from "lucide-react";
import { api, fmtINR, SEV } from "../services/api";

export default function InsightCard({ insight, defaultOpen, onOpenGraph }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fb, setFb] = useState({ useful: null, correct: null });
  const sev = SEV[insight.severity];

  useEffect(() => { if (defaultOpen) setOpen(true); }, [defaultOpen]);

  useEffect(() => {
    if (open && !detail) {
      setLoading(true);
      api.insight(insight.id).then((d) => { setDetail(d); setLoading(false); });
      api.getFeedback(insight.id).then((f) => setFb({ useful: f.useful, correct: f.correct }));
    }
  }, [open]); // eslint-disable-line

  const sendFb = async (patch) => {
    const next = { ...fb, ...patch };
    setFb(next);
    await api.feedback({ insight_id: insight.id, ...next });
  };

  return (
    <article className={`insight-card ${open ? "open" : ""}`} style={{ "--sev": sev.color }}>
      <button className="insight-head" onClick={() => setOpen((v) => !v)}>
        <span className="sev-badge" style={{ background: sev.bg, color: sev.color, borderColor: sev.ring }}>{insight.severity}</span>
        <div className="insight-head-main">
          <div className="insight-title">{insight.title}</div>
          <div className="insight-sub">
            <span>{insight.event_count} correlated signals</span>
            <span className="sep">·</span>
            <span>{insight.systems.length} systems</span>
            <span className="sep">·</span>
            <span>confidence {insight.confidence}%</span>
          </div>
        </div>
        <div className="insight-exp">
          <div className="exp-val">{fmtINR(insight.exposure)}</div>
          <div className="exp-lbl">exposure</div>
        </div>
        <ChevronDown size={18} className={`chev ${open ? "open" : ""}`} />
      </button>

      {open && (
        <div className="insight-body">
          {loading || !detail ? (
            <div className="insight-loading">Generating explanation…</div>
          ) : (
            <>
              <div className="objectives">
                {detail.objectives.map((o) => <span key={o} className="obj-chip"><Target size={12} /> {o}</span>)}
              </div>

              <div className="ai-block">
                <div className="ai-row">
                  <div className="ai-icon"><Sparkles size={15} /></div>
                  <div>
                    <div className="ai-label">Why it matters</div>
                    <p>{detail.explanation?.why_it_matters}</p>
                  </div>
                </div>
                <div className="ai-row">
                  <div className="ai-icon"><GitBranch size={15} /></div>
                  <div>
                    <div className="ai-label">What DotIQ connected</div>
                    <p>{detail.explanation?.what_dotiq_connected}</p>
                  </div>
                </div>
                <div className="ai-row">
                  <div className="ai-icon rec"><Search size={15} /></div>
                  <div>
                    <div className="ai-label">Recommended investigation</div>
                    <p>{detail.explanation?.recommended_investigation}</p>
                  </div>
                </div>
              </div>

              <div className="reasoning-path">
                <div className="rp-label">Reasoning path</div>
                <div className="rp-chain">
                  {detail.path_nodes.map((n, i) => (
                    <React.Fragment key={n.id}>
                      <span className={`rp-node t-${n.type}`}>{n.label}</span>
                      {i < detail.path_nodes.length - 1 && <span className="rp-arrow">→</span>}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <div className="evidence-grid">
                <div className="ev-side">
                  <div className="ev-h">Cross-system evidence</div>
                  <ul className="ev-list">
                    {detail.events.map((e) => (
                      <li key={e.id}>
                        <span className="ev-src">{e.source}</span>
                        <span className="ev-what">{e.what}</span>
                        <span className="ev-conf">{e.confidence}%</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {detail.affected_orders?.length > 0 && (
                  <div className="ev-side">
                    <div className="ev-h">Open-order exposure</div>
                    <ul className="order-list">
                      {detail.affected_orders.map((o) => (
                        <li key={o.order}>
                          <span className="ord-id">{o.order}</span>
                          <span className="ord-dealer">{o.dealer}</span>
                          <span className="ord-qty">{o.qty.toLocaleString("en-IN")} sqm</span>
                          <span className="ord-val">{fmtINR(o.value)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="insight-actions">
                <button className="btn-ghost sm" onClick={() => onOpenGraph(insight.reasoning_path?.[0] || insight.dealers?.[0])}>
                  <Network size={15} /> View on context graph
                </button>
                <div className="fb-group">
                  <span className="fb-label">Useful?</span>
                  <button className={`fb-btn ${fb.useful === true ? "on-good" : ""}`} onClick={() => sendFb({ useful: true })}><ThumbsUp size={14} /></button>
                  <button className={`fb-btn ${fb.useful === false ? "on-bad" : ""}`} onClick={() => sendFb({ useful: false })}><ThumbsDown size={14} /></button>
                  <span className="fb-label">Correlation</span>
                  <button className={`fb-btn ${fb.correct === true ? "on-good" : ""}`} onClick={() => sendFb({ correct: true })}><Check size={14} /></button>
                  <button className={`fb-btn ${fb.correct === false ? "on-bad" : ""}`} onClick={() => sendFb({ correct: false })}><X size={14} /></button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </article>
  );
}
